import { AnyID, SceneObjects } from '@types'
import { Matrix3, Matrix4, Quaternion, Vector3 } from 'three'

/**
 * Calcula la OBB de un ItemGroup en espacio local del grupo,
 * creando el paralelepípedo orientado más pequeño que contiene todos los hijos.
 */
export function computeItemGroupOBB(
  children: Set<AnyID>,
  items: Map<AnyID, SceneObjects>,
  groupMatrix: Matrix4,
): { center: Vector3; halfSize: Vector3; rotation: Matrix3 } | null {
  // Los hijos tienen matrices LOCALES en el store (relativas al grupo).
  // Calculamos el AABB en espacio local del grupo.
  const cornerOffsets = [
    [-1, -1, -1],
    [1, -1, -1],
    [1, 1, -1],
    [-1, 1, -1],
    [-1, -1, 1],
    [1, -1, 1],
    [1, 1, 1],
    [-1, 1, 1],
  ] as const

  const minLocal = new Vector3(Infinity, Infinity, Infinity)
  const maxLocal = new Vector3(-Infinity, -Infinity, -Infinity)
  let hasAny = false

  const tmpQuat = new Quaternion()
  const tmpPos = new Vector3()
  const tmpScale = new Vector3()

  children.forEach((childId) => {
    const child = items.get(childId) as any
    if (!child?.matrix || child.width == null || child.height == null || child.thickness == null)
      return
    hasAny = true

    // La matriz del hijo es local al grupo
    const childLocalMatrix = child.matrix as Matrix4
    childLocalMatrix.decompose(tmpPos, tmpQuat, tmpScale)

    // Centro del hijo en espacio local del grupo
    const itemCenter = new Vector3(child.width / 2, child.height / 2, child.thickness / 2)
      .applyQuaternion(tmpQuat)
      .add(tmpPos)

    const hw = child.width / 2
    const hh = child.height / 2
    const ht = child.thickness / 2

    const rotMat3 = new Matrix3().setFromMatrix4(new Matrix4().makeRotationFromQuaternion(tmpQuat))

    // Expandir las 8 esquinas del hijo en espacio local del grupo
    for (const [sx, sy, sz] of cornerOffsets) {
      const localCorner = new Vector3(sx * hw, sy * hh, sz * ht)
        .applyMatrix3(rotMat3)
        .add(itemCenter)
      minLocal.min(localCorner)
      maxLocal.max(localCorner)
    }
  })

  if (!hasAny) return null

  // Centro y halfSize en espacio local del grupo
  const centerLocal = new Vector3().addVectors(minLocal, maxLocal).multiplyScalar(0.5)
  const halfSize = new Vector3().subVectors(maxLocal, minLocal).multiplyScalar(0.5)

  // Extraer posición y rotación del grupo
  const groupPos = new Vector3()
  const groupQuat = new Quaternion()
  groupMatrix.decompose(groupPos, groupQuat, tmpScale)

  // Transformar el centro del espacio local al espacio mundo
  const centerWorld = centerLocal.clone().applyQuaternion(groupQuat).add(groupPos)

  // La rotación de la OBB es la rotación del grupo
  const groupRotation = new Matrix3().setFromMatrix4(
    new Matrix4().makeRotationFromQuaternion(groupQuat),
  )

  return { center: centerWorld, halfSize, rotation: groupRotation }
}
